import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ActivityIndicator, FlatList, Image, ScrollView, StyleSheet, View} from 'react-native';
import {useFocusEffect, useIsFocused} from '@react-navigation/native';
import {ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import {ErrorBanner} from '../../../components/ErrorBanner';
import {ProductTileCarousel} from '../../../components/ProductTile';
import {OfferDetailSkeleton, SkeletonGroup} from '../../../components/skeleton';
import Button from '../../../components/themed/Button';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import Markdown from '../../../components/ui-controls/Markdown';
import Text from '../../../components/ui-controls/Text';
import config from '../../../config';
import {space, type} from '../../../design-system/tokens';
import {useTabBarBottomPadding} from '../../../hooks/useTabBarBottomPadding';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {type NestedRouteParams, offerDetailRoute} from '../../../navigation/offerRoute';
import {OfferDetailStore} from '../../../stores/offersStore';
import type {MembershipStatus} from '../../../stores/rewardsStore';
import {useStores} from '../../../stores/use-stores';
import theme from '../../../style/theme';
import rewardsTheme from '../../../theme/rewardsTheme';
import {getAwaitingDealMessage, isCoffeeCardUnearned} from '../../../utils/coffeeCard';
import {logger} from '../../../utils/logger';
import {getRecoveryButtonLabel, getRecoveryRoute} from '../../../utils/offerErrorHandler';
import {resetToMainScreen} from '../../rewards/resetToHome';

import CoffeeCard from './CoffeeCard';
import OfferNotFound from './OfferNotFound';
import OfferNotReady from './OfferNotReady';
import {type OfferScreenState, resolveOfferScreenState} from './offerScreenState';
import OfferSignInPanel from './OfferSignInPanel';
import OfferStoreRequired from './OfferStoreRequired';
import {useUnlockingWindow} from './useUnlockingWindow';

type ExclusiveOfferDetailScreenStandaloneProps = {
  offerDetails?: OfferDetailStore;
  isLoading?: boolean;
  isNotFound?: boolean;
  navigation: RouteNavigatorScreenProps<AppRoute.EXCLUSIVE_OFFER_DETAIL>['navigation'];
};
export const ExclusiveOfferDetailScreenStandalone = observer(
  ({
    offerDetails,
    isLoading,
    isNotFound,
    navigation,
  }: ExclusiveOfferDetailScreenStandaloneProps) => {
    const {cartStore, shopSelectionStore, rewardsStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();
    const tabBarPadding = useTabBarBottomPadding();
    const isApplied = cartStore.isOfferApplied(offerDetails?.offer.mflId);
    const {applyError} = offerDetails || {};

    // DIS-1917: SkeletonGroup announces the skeleton as busy loading content.
    // No cross-fade, so the skeleton appears and leaves as it did before.
    const loadingSkeleton = (
      <ContentView testID="exclusive-offer-detail-screen-loading">
        <SkeletonGroup isLoading crossFade={false} skeleton={<OfferDetailSkeleton />}>
          {null}
        </SkeletonGroup>
      </ContentView>
    );

    if (isLoading) {
      return loadingSkeleton;
    }

    if (isNotFound) {
      return (
        <ContentView testID="exclusive-offer-detail-screen-not-found">
          <OfferNotFound
            navigation={navigation}
            isConfirmedNonMember={rewardsStore.membershipStatus === 'not_member'}
          />
        </ContentView>
      );
    }

    if (!offerDetails?.offer) {
      return loadingSkeleton;
    }

    const hasBarcodes = offerDetails.offer.barcodes && offerDetails.offer.barcodes.length > 0;
    const isLoadingProducts = offerDetails.isLoadingLinkedProducts;
    const hasProducts = offerDetails.linkedProducts && offerDetails.linkedProducts.length > 0;
    const hasBarcodesButNoProducts =
      hasBarcodes && offerDetails.linkedProducts !== null && !hasProducts;

    // DIS-1661 Task D3: a coffee card that has used up all its redemptions
    // still 403s if applied — isFullyRedeemed reached the client already
    // (offer.coffeeCardStats.fully_redeemed) but nothing read it.
    const isFullyRedeemed =
      offerDetails.offer.offerTrigger === 'Coffee Card' &&
      offerDetails.offer.coffeeCardStats.fully_redeemed;

    // DIS-1729: a coffee card is recursive — redeeming it resets the bottles to
    // empty while fully_redeemed stays false (that flag is the card LIMIT, not
    // the cycle), so APPLY went live again on a card the member has not earned.
    const isUnearned = isCoffeeCardUnearned(offerDetails.offer);

    // DIS-1912: an earned card waits for MFL to list the reward deal.
    const awaitingDealMessage = getAwaitingDealMessage(offerDetails.offer);

    // Simple disabled logic - no client-side eligibility validation (server handles it)
    // DIS-1661: no mflId means there is nothing to apply against — apply is
    // keyed on the MFL deal id — so the button cannot be live for such an offer.
    const isButtonDisabled =
      offerDetails.isApplyLoading ||
      isApplied ||
      hasBarcodesButNoProducts ||
      isFullyRedeemed ||
      isUnearned ||
      !!awaitingDealMessage ||
      !offerDetails.offer.mflId; // Keep product availability check for UX

    const getRecoveryAction = () => {
      if (!applyError?.recoveryAction || applyError.recoveryAction === 'retry') {
        return null;
      }

      const label = getRecoveryButtonLabel(applyError.recoveryAction);
      const route = getRecoveryRoute(applyError.recoveryAction);

      if (!label || !route) return null;

      return {
        label,
        onPress: () => navigation.navigate(route),
      };
    };

    const image = isWebDesktopScreen
      ? offerDetails?.offer?.heroImageDesktop?.[0]
      : offerDetails?.offer?.heroImage?.[0];

    const setButtonTitle = () => {
      if (hasBarcodesButNoProducts) {
        return 'Offer currently not available';
      }
      if (isFullyRedeemed) {
        return 'Fully Redeemed';
      }
      return isApplied ? 'APPLIED' : 'APPLY';
    };

    return (
      <ContentView testID="exclusive-offer-detail-screen">
        {/* DIS-1661 Task D1: rendered above the ScrollView, not inside it — a
            failed apply previously pushed this banner roughly one viewport
            below the fold, inside a ScrollView that does not scroll on this
            screen, making it permanently invisible. Keeping it outside the
            scroll content guarantees it is in view the moment apply fails. */}
        {/* DIS-1911: select_store opens the store selector from APPLY instead. */}
        {applyError?.recoverable && applyError.recoveryAction !== 'select_store' ? (
          <ErrorBanner
            message={applyError.message}
            action={getRecoveryAction()}
            testID="apply-offer-error"
          />
        ) : null}
        <ScrollView
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{paddingBottom: tabBarPadding}}>
          {image?.url ? (
            <Image
              source={{uri: image.url}}
              style={{
                width: '100%',
                height: undefined,
                aspectRatio: image.width && image.height ? image.width / image.height : 1.52,
              }}
              resizeMode="cover"
            />
          ) : null}

          <View style={styles.textCard}>
            <Text style={styles.headline}>{offerDetails?.offer?.headline}</Text>
            <Markdown>{offerDetails?.offer?.description}</Markdown>
          </View>

          {offerDetails?.offer?.offerTrigger === 'Coffee Card' && (
            <CoffeeCard offer={offerDetails.offer} />
          )}

          {awaitingDealMessage ? (
            <Text style={styles.awaitingDeal} testID="exclusive-offer-awaiting-deal-message">
              {awaitingDealMessage}
            </Text>
          ) : null}

          <Button
            onPress={async () => {
              const result = await offerDetails.applyOffer();
              if (result.error?.recoveryAction === 'select_store') {
                navigation.navigate(AppRoute.HOME, {openSuburbModal: true});
              }
            }}
            loading={offerDetails.isApplyLoading}
            disabled={isButtonDisabled}
            title={setButtonTitle()}
            buttonStyle={styles.enterHereButton}
          />
          {hasBarcodes && !shopSelectionStore.ecommerceDisabled ? (
            isLoadingProducts ? (
              <ActivityIndicator
                style={styles.linkedProductsActivityIndicator}
                testID="linked-products-loading"
              />
            ) : hasProducts ? (
              <ThemeProvider theme={rewardsTheme}>
                <FlatList
                  data={offerDetails.linkedProducts}
                  keyExtractor={item => String(item.id)}
                  ItemSeparatorComponent={ItemSeparatorComponent}
                  style={styles.flatList}
                  renderItem={({item}) => {
                    return (
                      <ProductTileCarousel
                        key={item.id}
                        item={item}
                        testContext="exclusive-offer"
                      />
                    );
                  }}
                  horizontal
                />
              </ThemeProvider>
            ) : (
              <View style={styles.textCard}>
                <Text>
                  The product(s) associated to this offer are currently not available in this store.
                </Text>
              </View>
            )
          ) : null}
        </ScrollView>
      </ContentView>
    );
  },
);

/** S4 and S6–S9: states a foreground session probe never replaces. */
const SETTLED_STATES: OfferScreenState[] = [
  'offer',
  'not_ready_error',
  'unlocking',
  'not_ready_empty',
  'finished',
];

type NestedNavigation = {navigate: (route: AppRoute, params: NestedRouteParams) => void};

export type ExclusiveOfferDetailScreenProps =
  RouteNavigatorScreenProps<AppRoute.EXCLUSIVE_OFFER_DETAIL> & {
    onOnlineOrderAfterSubmit?: () => void;
    onOnlineOrderCancel?: () => void;
    isLastCompetitionRemaining: boolean; // Bundy Power Plays
  };

const ExclusiveOfferDetailScreen = observer(
  ({route, navigation}: ExclusiveOfferDetailScreenProps) => {
    const rootStore = useStores();
    const {offersStore, userStore, shopSelectionStore, rewardsStore} = rootStore;
    const isFocused = useIsFocused();

    const [offerDetails, setOfferDetails] = useState<OfferDetailStore>();

    // Refresh offers on screen focus so canApply/disabledReason stay in sync with server
    useFocusEffect(
      useCallback(() => {
        offersStore.refreshOffers();
      }, [offersStore]),
    );

    // DIS-1914: back on the offer means the customer left the store picker
    // without choosing, so drop the return route "Choose store" set.
    useFocusEffect(
      useCallback(() => {
        shopSelectionStore.consumePostSelectionReturnRoute();
      }, [shopSelectionStore]),
    );

    const offerParam = route.params?.offerId;

    // DIS-1917 § 4.1: arrival on this offer, by any route, ends its intent.
    // offerReturn is read at focus time and is not a dependency, so the intent
    // a panel action writes while this screen is focused survives the navigation.
    useFocusEffect(
      useCallback(() => {
        if (userStore.offerReturn?.offerId === offerParam) userStore.clearOfferReturn();
      }, [offerParam, userStore]),
    );

    // DIS-1917 gap A: the sign-in marker lives from "Sign in to view my offer"
    // to the next time this offer is on screen. A store field, not a route
    // param, so it stays out of the web URL and cannot replay from history.
    // Focused while signed out means the sign-in was abandoned; leaving while
    // signed in means the marker had its one chance. Session is read at call
    // time, not as a dependency.
    useFocusEffect(
      useCallback(() => {
        const isOurs = () => userStore.offerSignInFrom === offerParam;
        if (isOurs() && userStore.sessionState === 'none') userStore.setOfferSignInFrom(null);
        return () => {
          if (isOurs() && userStore.sessionState !== 'none') userStore.setOfferSignInFrom(null);
        };
      }, [offerParam, userStore]),
    );

    // DIS-1914: only the first resolution on this mount waits on the
    // foreground session probe. Once a settled state has shown, a probe changes
    // nothing on screen and the offer's detail store is not rebuilt.
    const hasSettled = useRef(false);

    const screenInputs = {
      offerParam,
      isRevalidating: rootStore.sessionProbeInFlight && !hasSettled.current,
      sessionState: userStore.sessionState,
      hasSelectedStore: !!shopSelectionStore.selectedStore,
      offersStatus: offersStore.offersStatus,
      data: offersStore.data,
      membershipStatus: rewardsStore.membershipStatus,
      isMembershipLoading: rewardsStore.isMembershipLoading,
    };

    // DIS-1914: the window opens on the state the screen would show with it
    // closed, so it only ticks once every earlier gate (session, store) passes.
    // DIS-1917 § 5.3: a confirmed non-member has no deals to wait for.
    const unlockingWindow = useUnlockingWindow({
      isEmpty:
        resolveOfferScreenState({...screenInputs, unlockingWindowOpen: false}).state ===
          'not_ready_empty' && rewardsStore.membershipStatus !== 'not_member',
      isFocused,
      isLoading: offersStore.isLoading,
      refresh: offersStore.refreshOffers,
    });
    const {state, offer, viewer} = resolveOfferScreenState({
      ...screenInputs,
      unlockingWindowOpen: unlockingWindow.open,
    });
    // The state this screen showed on its last committed render.
    const lastShownState = useRef<OfferScreenState | undefined>(undefined);
    // The viewer the last event carried (DIS-1917 § 6.3).
    const lastLoggedViewer = useRef<MembershipStatus | undefined>(undefined);
    // A load inside the window (its own fast refresh, or a store retry) keeps
    // the data and sets offersStatus to 'loading'. The screen keeps showing
    // "Unlocking" through it, and the log does not count it as a new state.
    // DIS-1928: a confirmed non-member never gets that frame.
    // After the window closes, a background reload (store retry, foreground
    // refresh) keeps the not-ready panel up in the same way.
    const shownState: OfferScreenState =
      state === 'loading' && unlockingWindow.open && rewardsStore.membershipStatus !== 'not_member'
        ? 'unlocking'
        : state === 'loading' &&
            lastShownState.current === 'not_ready_empty' &&
            offersStore.data !== null
          ? 'not_ready_empty'
          : state;

    useEffect(() => {
      if (SETTLED_STATES.includes(shownState)) hasSettled.current = true;
    }, [shownState]);

    useEffect(() => {
      if (offer) {
        setOfferDetails(new OfferDetailStore(offer, rootStore));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offer?.offerId]);

    // Handle deep linking "404". TODO: Better way? In "linking.ts"?
    // Only a missing route param is a bad deep link. An offer that simply has
    // no mflId is valid (DIS-1661) and must render, not bounce to HOME.
    useEffect(() => {
      if (!offerParam) {
        navigation.navigate(AppRoute.HOME);
      }
    }, [navigation, offerParam]);

    // DIS-1914: one event per state change. The offer code is the only id the
    // spec allows, so the event carries a flag instead of the param itself.
    // A failed load adds its HTTP status only (a number, or undefined with no
    // response), so 401, 5xx and timeouts can be told apart without message text.
    // DIS-1917: a viewer change inside one state (row 2 to row 3) logs again.
    const loadErrorStatus = offersStore.lastLoadError?.status;
    useEffect(() => {
      if (lastShownState.current === shownState && lastLoggedViewer.current === viewer) return;
      lastShownState.current = shownState;
      lastLoggedViewer.current = viewer;
      logger.info('ExclusiveOfferDetail', 'offer_deeplink_state', {
        state: shownState,
        tenant: config.BASE_TENANT,
        hasOfferParam: !!offerParam,
        viewer,
        ...(shownState === 'not_ready_error' ? {loadErrorStatus} : {}),
      });
    }, [shownState, viewer, offerParam, loadErrorStatus]);

    // DIS-1917 gap A: after "Sign in to view my offer", a confirmed non-member
    // goes to the Rewards page (JOIN NOW) instead of row 3. Confirmed means the
    // same test row 3 uses (viewer `not_member` on not_ready_empty), so a
    // lookup still loading or failed (`unknown`) never redirects. A root reset,
    // so Back from Rewards cannot reach this screen and redirect again. The
    // arrival cleared the intent; it is set again so the join returns here.
    const cameFromOfferSignIn = !!offerParam && userStore.offerSignInFrom === offerParam;
    useEffect(() => {
      if (!isFocused || !cameFromOfferSignIn || !offerParam) return;
      if (shownState !== 'not_ready_empty' || viewer !== 'not_member') return;
      userStore.setOfferSignInFrom(null);
      userStore.setOfferReturn(offerParam);
      resetToMainScreen(AppRoute.REWARDS, !!shopSelectionStore.selectedStore);
    }, [
      isFocused,
      cameFromOfferSignIn,
      offerParam,
      shownState,
      viewer,
      userStore,
      shopSelectionStore,
    ]);

    // DIS-1914: sign in and sign up share one entry screen, which offers both
    // and calls afterLogin(), so the return route brings the member back here.
    const openSignInReturningToOffer = () => {
      if (!offerParam) return;
      userStore.setOfferReturn(offerParam);
      userStore.setOfferSignInFrom(offerParam);
      userStore.setPostLoginReturnToScreen(...offerDetailRoute(offerParam));
      // ROOT is typed `undefined` in the param list, so the nested target
      // cannot be typed through the screen's navigation prop.
      (navigation as unknown as NestedNavigation).navigate(AppRoute.ROOT, {
        screen: AppRoute.MAIN,
        params: {screen: AppRoute.ACCOUNT_NOT_LOGGED_IN},
      });
    };

    // DIS-1917 § 3: the join flows end on screens that read the intent, not
    // postLoginReturnToScreen, so these set the intent. S3 Join and row 3 Learn
    // first clear a stale return route (an abandoned cart or gift-card sign-in):
    // afterLogin() opens that route before the intent, so the newer offer
    // intent would lose on the CREATE ACCOUNT → existing-email sign-in.
    const openRewardsReturningToOffer = () => {
      if (!offerParam) return;
      userStore.setPostLoginReturnToScreen(null);
      userStore.setOfferReturn(offerParam);
      navigation.navigate(AppRoute.REWARDS);
    };

    // The same target as JOIN NOW for a signed-in viewer (JoinMoreRewardsModal).
    const openActivateReturningToOffer = () => {
      if (!offerParam) return;
      userStore.setOfferReturn(offerParam);
      (navigation as unknown as NestedNavigation).navigate(AppRoute.ROOT_REWARDS_FLOW, {
        screen: AppRoute.REWARDS_ACTIVATE_TERMS_AND_CONDITIONS,
      });
    };

    // Only an empty list reopens the window. After a load error (S6) the
    // window would make a fast refresh behind the not-ready panel.
    // DIS-1928: a failed membership lookup stays `unknown`. Try again runs it again.
    const tryAgain = () => {
      offersStore.refreshOffers();
      if (rewardsStore.membershipStatus === 'unknown') {
        rewardsStore.loadRewardsForUser(userStore.phoneNumber);
      }
      if (shownState === 'not_ready_empty') unlockingWindow.reopen();
    };

    const seeAllOffers = () => navigation.navigate(AppRoute.EXCLUSIVE_OFFER_LIST);

    // DIS-1914: the store picker the home screen opens. With no store selected
    // ROOT is mounted and ROOT_STORE_SELECTION_FLOW is not (App.tsx), so the
    // picker is Home's suburb modal. The store list consumes the return route.
    // SHORTCUT: S2b shows only in the case named at
    // shopSelectionStore.postSelectionReturnRoute; other no-store offer links
    // never mount this screen and return through the delayedNavigationPath
    // replay. Upgrade path: remove this handler if the replay covers every case.
    const chooseStoreReturningToOffer = () => {
      if (!offerParam) return;
      shopSelectionStore.setPostSelectionReturnRoute(offerDetailRoute(offerParam));
      navigation.navigate(AppRoute.HOME, {openSuburbModal: true});
    };

    switch (shownState) {
      case 'store_required':
        return (
          <ContentView>
            <OfferStoreRequired onChooseStore={chooseStoreReturningToOffer} />
          </ContentView>
        );
      case 'sign_in_required':
        return (
          <ContentView>
            <OfferSignInPanel
              onSignIn={openSignInReturningToOffer}
              onJoin={openRewardsReturningToOffer}
            />
          </ContentView>
        );
      case 'not_ready_error':
      case 'not_ready_empty':
        return (
          <ContentView>
            {/* S6 ignores the variant. S8 shows the join panel only to a
                confirmed non-member; `unknown` gets the member copy (row 6). */}
            <OfferNotReady
              kind={shownState === 'not_ready_error' ? 'error' : 'empty'}
              variant={viewer === 'not_member' ? 'not_member' : 'member'}
              onTryAgain={tryAgain}
              onSeeAllOffers={seeAllOffers}
              onJoin={openActivateReturningToOffer}
              onLearnMore={openRewardsReturningToOffer}
            />
          </ContentView>
        );
      case 'unlocking':
        return (
          <ContentView testID="exclusive-offer-unlocking">
            {/* One accessible element, so a screen reader announces the state
                once, politely, when it appears. */}
            <View
              accessible
              accessibilityRole="progressbar"
              accessibilityLabel="Unlocking your offer…"
              accessibilityLiveRegion="polite">
              <Text style={styles.unlockingText}>Unlocking your offer…</Text>
              <OfferDetailSkeleton />
            </View>
          </ContentView>
        );
      default:
        // 'missing_param', 'checking', 'loading', 'offer' and 'finished'. The
        // offer stays on the loading view until its detail store is created.
        return (
          <ExclusiveOfferDetailScreenStandalone
            isLoading={shownState !== 'offer' && shownState !== 'finished'}
            isNotFound={shownState === 'finished'}
            offerDetails={shownState === 'offer' ? offerDetails : undefined}
            navigation={navigation}
          />
        );
    }
  },
);

function ItemSeparatorComponent() {
  return <View style={styles.separator} />;
}

export default ExclusiveOfferDetailScreen;

const styles = StyleSheet.create({
  textCard: {
    marginTop: 24,
  },
  awaitingDeal: {
    textAlign: 'center',
    marginBottom: space.s16,
  },
  termsAndConditions: {
    marginTop: 16,
    alignItems: 'center',
  },
  termsAndConditionsLink: {
    fontSize: 14,
    color: theme.colour.text.link.primary,
  },
  enterText: {
    fontSize: 16,
    color: 'white',
  },
  headline: {
    fontSize: 20,
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  flatList: {
    marginTop: 32,
    marginBottom: 4,
  },
  enterHereButton: {
    width: '80%',
  },
  copy: {
    fontSize: 16,
  },
  unlockingText: {
    ...type.paragraph.p2,
    textAlign: 'center',
    marginBottom: space.s16,
  },
  linkedProductsActivityIndicator: {
    marginVertical: 16,
  },
  separator: {
    width: 8,
    height: 8,
  },
});
